/**
 * Ticket Type Enum - Matches backend C# enum TicketType
 * Must be kept in sync with backend enum values
 * 
 * IMPORTANT: 
 * - When CREATING tickets, use string values ("SaleTicket" or "RepairTicket")
 * - When READING tickets from backend, you may receive string values ("SaleTicket", "RepairTicket")
 * - Use helper functions below to handle both formats for display
 */
export const TICKET_TYPE = {
  SALE: 'SaleTicket',        // SaleTicket - Regular POS sale
  REPAIR: 'RepairTicket',    // RepairTicket - Service/Repair ticket
};

/**
 * Normalize ticket type to consistent string format
 * Handles both integer and string formats from backend
 * @param {number|string} ticketType - Ticket type (0, 1, "SaleTicket", "RepairTicket")
 * @returns {string} Normalized ticket type ("SaleTicket" or "RepairTicket")
 */
export const normalizeTicketType = (ticketType) => {
  // Already a string in correct format
  if (typeof ticketType === 'string') {
    const normalized = ticketType.toLowerCase();
    if (normalized === 'repairticket' || normalized === 'repair') {
      return TICKET_TYPE.REPAIR;
    }
    if (normalized === 'saleticket' || normalized === 'sale') {
      return TICKET_TYPE.SALE;
    }
  }
  
  // Legacy integer format from backend
  if (typeof ticketType === 'number') {
    return ticketType === 1 ? TICKET_TYPE.REPAIR : TICKET_TYPE.SALE;
  }
  
  // Default to SALE
  return TICKET_TYPE.SALE;
};

/**
 * Get ticket type label for display
 * Handles both integer and string formats
 * @param {number|string} ticketType - Ticket type (0, 1, "SaleTicket", "RepairTicket")
 * @param {Function} t - Translation function
 * @returns {string} Translated ticket type label
 */
export const getTicketTypeLabel = (ticketType, t) => {
  const normalized = normalizeTicketType(ticketType);
  
  switch (normalized) {
    case TICKET_TYPE.REPAIR:
      return t('repair_ticket') || 'Repair Ticket';
    case TICKET_TYPE.SALE:
    default:
      return t('sale_ticket') || 'Sale Ticket';
  }
};

/**
 * Get ticket type color for Chip/Badge display
 * Handles both integer and string formats
 * @param {number|string} ticketType - Ticket type (0, 1, "SaleTicket", "RepairTicket")
 * @returns {string} MUI color name
 */
export const getTicketTypeColor = (ticketType) => {
  const normalized = normalizeTicketType(ticketType);
  
  switch (normalized) {
    case TICKET_TYPE.REPAIR:
      return 'secondary';
    case TICKET_TYPE.SALE:
    default:
      return 'primary';
  }
};

export default TICKET_TYPE;
